import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  TrendingUp,
  Trophy,
  Percent,
  Calendar,
  ArrowRight,
  Sparkles,
  Plus,
  Receipt,
  Bot,
  Ticket,
  AlertTriangle,
  AlertCircle,
  FileText,
  Eye,
  Printer,
  Download,
  Wrench,
  ClipboardList,
  Cloud,
  CloudOff,
  CheckCircle2,
  MessageCircle,
  Landmark,
} from "lucide-react";
import { api, apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { formatCurrency, formatDate, daysUntil } from "@/lib/format";
import { BidStatus, Spinner } from "@/components/UI";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [feeds, setFeeds] = useState({ egp: [], ungm: [] });
  const [ticketStats, setTicketStats] = useState({ total: 0, open: 0, sla_breached: 0 });
  const [recentDocs, setRecentDocs] = useState({ quotation: [], job_card: [], sdn: [] });
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [backup, setBackup] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [wa, setWa] = useState(null);
  const [waTesting, setWaTesting] = useState(false);
  const [genBusy, setGenBusy] = useState(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const nav = useNavigate();

  const loadDmsDocs = useCallback(() => {
    Promise.all([
      api.listDmsDocs({ doc_type: "quotation" }),
      api.listDmsDocs({ doc_type: "job_card" }),
      api.listDmsDocs({ doc_type: "sdn" }),
    ])
      .then(([q, j, s]) =>
        setRecentDocs({
          quotation: q.slice(0, 3),
          job_card: j.slice(0, 3),
          sdn: s.slice(0, 3),
        })
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    const reload = () => {
      api.dashboard().then(setData).catch(() => {});
      api.ticketsStats().then(setTicketStats).catch(() => {});
      api.expiringDocuments(60).then(setExpiringDocs).catch(() => {});
      loadDmsDocs();
    };
    api
      .dashboard()
      .then(setData)
      .catch((e) =>
        toast(e?.response?.data?.detail || "Failed to load dashboard", "error")
      );
    Promise.all([api.feed("egp"), api.feed("ungm")])
      .then(([egp, ungm]) =>
        setFeeds({ egp: egp.items.slice(0, 3), ungm: ungm.items.slice(0, 2) })
      )
      .catch(() => {});
    api.ticketsStats().then(setTicketStats).catch(() => {});
    api.expiringDocuments(60).then(setExpiringDocs).catch(() => {});
    if (user?.role === "admin") {
      api.backupStatus().then(setBackup).catch(() => {});
      api.whatsappStatus().then(setWa).catch(() => {});
    }
    loadDmsDocs();
    // refresh when tab regains focus
    const handleFocus = () => reload();
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) reload();
    });
    return () => window.removeEventListener("focus", handleFocus);
  }, [toast, user?.role, loadDmsDocs]);

  const handleDocAction = async (d, mode) => {
    setGenBusy(d.id + mode);
    try {
      await api.generateDmsPdf(d.id);
      const res = await apiClient.get(`/dms/documents/${d.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (mode === "preview") {
        window.open(url, "_blank");
      } else if (mode === "print") {
        const w = window.open(url, "_blank");
        if (w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch { /* print blocked */ } });
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${d.serial.replace(/\//g, "_")}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast("PDF ready", "success");
      }
    } catch (e) {
      toast("PDF generation failed", "error");
    } finally {
      setGenBusy(null);
    }
  };

  if (!data)
    return (
      <div className="flex items-center gap-2 muted text-sm">
        <Spinner /> Loading dashboard…
      </div>
    );

  const t = data.totals;
  const wonAmounts = data.won_by_currency || {};
  const moneyOwed = data.money_owed || { outstanding_by_currency: {}, unpaid_count: 0, partial_count: 0 };
  const owedEntries = Object.entries(moneyOwed.outstanding_by_currency || {});
  const rev = data.revenue_by_month || null;
  const hasRev = rev && (rev.invoiced.some((v) => v > 0) || rev.collected.some((v) => v > 0));
  const revMax = hasRev ? Math.max(...rev.invoiced, ...rev.collected, 1) : 1;
  const REV_MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
  const firstName = (user?.full_name || user?.email || "").split(" ")[0] || "there";

  return (
    <div data-testid="page-dashboard" className="space-y-6">
      {/* Greeting */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="h1">
            {greeting()},{" "}
            <span style={{ color: "var(--green-700)" }}>{firstName}</span>
          </h1>
          <p className="muted text-sm mt-1">
            Here&apos;s what&apos;s happening across your bids and documents today
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="qa-new-bid"
            onClick={() => nav("/bids?new=1")}
            className="btn btn-ghost"
          >
            <Plus size={14} /> Bid
          </button>
          <button
            data-testid="qa-new-quote"
            onClick={() => nav("/dms/quotations?new=1")}
            className="btn btn-ghost"
          >
            <Receipt size={14} /> Quotation
          </button>
          <button
            data-testid="qa-ai"
            onClick={() => nav("/assistant")}
            className="btn btn-primary"
          >
            <Bot size={14} /> Ask AI
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Briefcase}
          label="Total Bids"
          value={t.bids_total}
          sub="All-time"
          testId="stat-total"
        />
        <StatCard
          icon={TrendingUp}
          label="Active"
          value={t.bids_active}
          sub="In pipeline"
          tone="amber"
          testId="stat-active"
        />
        <StatCard
          icon={Trophy}
          label="Won"
          value={t.bids_won}
          sub={
            Object.keys(wonAmounts).length
              ? Object.entries(wonAmounts)
                  .map(([c, v]) => formatCurrency(v, c))
                  .join(" · ")
              : "—"
          }
          tone="emerald"
          testId="stat-won"
        />
        <StatCard
          icon={Percent}
          label="Win rate"
          value={`${t.win_rate}%`}
          sub={`${t.bids_won} won · ${t.bids_lost} lost`}
          tone="green"
          testId="stat-winrate"
        />
      </div>

      {/* Service desk strip */}
      <div className="card card-padded flex flex-wrap items-center justify-between gap-4" data-testid="card-tickets-strip">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center"
               style={{ background: "var(--green-50)", color: "var(--green-700)" }}>
            <Ticket size={20} />
          </div>
          <div>
            <div className="text-sm font-semibold">Service Desk</div>
            <div className="text-xs muted">
              <b className="text-slate-900">{ticketStats.open}</b> open ticket{ticketStats.open === 1 ? "" : "s"}
              {ticketStats.sla_breached > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-rose-700 font-semibold">
                  <AlertTriangle size={11} /> {ticketStats.sla_breached} SLA breach{ticketStats.sla_breached === 1 ? "" : "es"}
                </span>
              )}
              {ticketStats.total > 0 && (
                <span className="ml-2">· {ticketStats.total} total</span>
              )}
            </div>
          </div>
        </div>
        <button
          data-testid="open-tickets"
          onClick={() => nav("/tickets")}
          className="btn btn-soft"
        >
          Open service queue <ArrowRight size={12} />
        </button>
      </div>

      {/* Money owed strip */}
      <div className="card card-padded flex flex-wrap items-center justify-between gap-4" data-testid="card-money-owed">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center"
               style={{ background: owedEntries.length ? "#fef2f2" : "var(--green-50)",
                        color: owedEntries.length ? "#dc2626" : "var(--green-700)" }}>
            <Landmark size={20} />
          </div>
          <div>
            <div className="text-sm font-semibold">Money Owed</div>
            <div className="text-xs muted" data-testid="money-owed-summary">
              {owedEntries.length === 0 ? (
                <span className="text-emerald-700 font-semibold">All accepted quotations are paid ✓</span>
              ) : (
                <>
                  <b className="text-red-600 text-sm">
                    {owedEntries.map(([c, v]) => formatCurrency(v, c)).join(" · ")}
                  </b>{" "}
                  outstanding
                  <span className="ml-2">
                    · {moneyOwed.unpaid_count} unpaid · {moneyOwed.partial_count} partially paid
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <button
          data-testid="open-money-owed"
          onClick={() => nav("/dms/quotations")}
          className="btn btn-soft"
        >
          Review quotations <ArrowRight size={12} />
        </button>
      </div>

      {/* Monthly cash flow — invoiced vs collected */}
      {hasRev && (
        <div className="card card-padded" data-testid="card-cashflow">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="text-sm font-semibold">Cash Flow — {rev.year}</div>
              <div className="text-xs muted">
                Invoiced <b className="text-slate-900">{formatCurrency(rev.invoiced.reduce((a, b) => a + b, 0), rev.currency)}</b>
                {" · "}Collected <b style={{ color: "var(--green-700)" }}>{formatCurrency(rev.collected.reduce((a, b) => a + b, 0), rev.currency)}</b>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[10px] font-semibold text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300 inline-block" /> Invoiced</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: "var(--green-700)" }} /> Collected</span>
            </div>
          </div>
          <div className="flex items-end gap-1.5 h-28" data-testid="cashflow-bars">
            {REV_MONTHS.map((m, i) => (
              <div key={`rev-${i}`} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                <div className="w-full flex items-end justify-center gap-0.5 h-24">
                  <div
                    className="w-1/2 max-w-[14px] rounded-t bg-slate-300"
                    style={{ height: `${(rev.invoiced[i] / revMax) * 100}%` }}
                    title={`Invoiced ${formatCurrency(rev.invoiced[i], rev.currency)}`}
                  />
                  <div
                    className="w-1/2 max-w-[14px] rounded-t"
                    style={{ height: `${(rev.collected[i] / revMax) * 100}%`, background: "var(--green-700)" }}
                    title={`Collected ${formatCurrency(rev.collected[i], rev.currency)}`}
                  />
                </div>
                <span className="text-[9px] muted">{m}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DMS Quick-PDF Center */}
      <div className="card" data-testid="card-dms">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="h2 flex items-center gap-2">
              <FileText size={16} style={{ color: "var(--green-600)" }} />
              Documents — Quick PDF
            </div>
            <div className="text-xs muted">Generate, print or download Quotations, Job Cards and Service Delivery Notes</div>
          </div>
          <button
            data-testid="bank-letter-btn"
            onClick={async () => {
              try {
                const res = await apiClient.get("/company/bank-letter-pdf", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = "Jero-One_Bank_Account_Details.pdf";
                a.click();
                URL.revokeObjectURL(url);
              } catch {
                toast("Could not generate the bank letter", "error");
              }
            }}
            className="btn btn-soft btn-sm"
            title="Signed & stamped bank account details letter on the letterhead"
          >
            <Landmark size={13} /> Bank Details Letter
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-0 border-t border-slate-100">
          {[
            { type: "quotation", route: "/dms/quotations", label: "Quotations", icon: Receipt, list: recentDocs.quotation },
            { type: "job_card", route: "/dms/job-cards", label: "Job Cards", icon: Wrench, list: recentDocs.job_card },
            { type: "sdn", route: "/dms/sdns", label: "Service Delivery Notes", icon: ClipboardList, list: recentDocs.sdn },
          ].map((col, idx) => (
            <div
              key={col.type}
              data-testid={`dms-col-${col.type}`}
              className={`p-4 ${idx < 2 ? "lg:border-r border-slate-100" : ""}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                       style={{ background: "var(--green-50)", color: "var(--green-700)" }}>
                    <col.icon size={14} />
                  </div>
                  <div className="font-semibold text-sm">{col.label}</div>
                </div>
                <button
                  data-testid={`dms-new-${col.type}`}
                  onClick={() => nav(`${col.route}?new=1`)}
                  className="btn btn-soft btn-sm"
                  title={`New ${col.label.replace(/s$/, "")}`}
                >
                  <Plus size={12} /> New
                </button>
              </div>

              {col.list.length === 0 ? (
                <div className="text-xs muted text-center py-6">
                  No {col.label.toLowerCase()} yet
                </div>
              ) : (
                <div className="space-y-2">
                  {col.list.map((d) => (
                    <div
                      key={d.id}
                      data-testid={`dms-row-${d.id}`}
                      className="border border-slate-100 rounded-lg p-2.5 hover:border-[#0e6939] transition"
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-mono text-[10px] font-bold" style={{ color: "var(--green-700)" }}>
                          {d.serial}
                        </span>
                        <span className={`badge dmsstatus-${d.status}`} style={{ fontSize: 9, padding: "1px 6px" }}>
                          {d.status}
                        </span>
                      </div>
                      <div className="text-xs font-medium truncate mb-1">
                        {d.client_name || "—"}
                      </div>
                      <div className="text-[10px] muted truncate mb-2">
                        {d.title || "No subject"}
                      </div>
                      <div className="flex items-center gap-1 border-t border-slate-100 pt-2">
                        <button
                          data-testid={`dms-preview-${d.id}`}
                          onClick={() => handleDocAction(d, "preview")}
                          disabled={genBusy === d.id + "preview"}
                          className="flex-1 text-[11px] py-1.5 rounded hover:bg-slate-50 flex items-center justify-center gap-1 text-slate-600"
                          title="Preview"
                        >
                          <Eye size={11} /> View
                        </button>
                        <button
                          data-testid={`dms-print-${d.id}`}
                          onClick={() => handleDocAction(d, "print")}
                          disabled={genBusy === d.id + "print"}
                          className="flex-1 text-[11px] py-1.5 rounded hover:bg-slate-50 flex items-center justify-center gap-1 text-slate-600"
                          title="Print"
                        >
                          <Printer size={11} /> Print
                        </button>
                        <button
                          data-testid={`dms-dl-${d.id}`}
                          onClick={() => handleDocAction(d, "download")}
                          disabled={genBusy === d.id + "download"}
                          className="flex-1 text-[11px] py-1.5 rounded hover:bg-slate-50 flex items-center justify-center gap-1 font-semibold"
                          style={{ color: "var(--green-700)" }}
                          title="Download PDF"
                        >
                          <Download size={11} /> PDF
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    data-testid={`dms-all-${col.type}`}
                    onClick={() => nav(col.route)}
                    className="w-full text-[11px] muted hover:text-[#0e6939] flex items-center justify-center gap-1 py-1 mt-1"
                  >
                    View all <ArrowRight size={10} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Upcoming deadlines */}
        <div className="card card-padded lg:col-span-2" data-testid="card-deadlines">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="h2 flex items-center gap-2">
                <Calendar size={16} style={{ color: "var(--green-600)" }} />
                Upcoming deadlines
              </div>
              <div className="text-xs muted">Bids closing soonest</div>
            </div>
            <button
              data-testid="view-all-bids"
              onClick={() => nav("/bids")}
              className="text-xs muted hover:text-[#0e6939] flex items-center gap-1"
            >
              All bids <ArrowRight size={11} />
            </button>
          </div>
          {data.upcoming_deadlines.length === 0 ? (
            <div className="muted text-sm py-6 text-center">
              No upcoming deadlines
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.upcoming_deadlines.map((b) => {
                const d = daysUntil(b.deadline);
                return (
                  <button
                    key={b.id}
                    onClick={() => nav(`/bids?id=${b.id}`)}
                    data-testid={`deadline-${b.id}`}
                    className="w-full flex items-center justify-between py-3 hover:bg-[var(--green-50)] px-2 rounded-lg text-left transition"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{b.title}</div>
                      <div className="text-xs muted truncate">
                        {b.client_name} · {b.ref_number || "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <BidStatus status={b.status} />
                      <div className="text-right min-w-[78px]">
                        <div className="text-xs font-semibold">
                          {formatDate(b.deadline)}
                        </div>
                        <div
                          className="text-[11px]"
                          style={{
                            color:
                              d <= 3
                                ? "#b91c1c"
                                : d <= 7
                                  ? "#b45309"
                                  : "var(--ink-500)",
                          }}
                        >
                          {d === 0 ? "Today" : d < 0 ? "Overdue" : `in ${d}d`}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Live opportunities */}
        <div className="card card-padded" data-testid="card-opps">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="h2 flex items-center gap-2">
                <Sparkles size={16} style={{ color: "var(--green-600)" }} />
                Live opportunities
              </div>
              <div className="text-xs muted">Fresh e-GP & UN bids</div>
            </div>
          </div>
          <div className="space-y-3">
            {[...feeds.egp.map((i) => ({ ...i, _src: "EGP" })), ...feeds.ungm.map((i) => ({ ...i, _src: "UNGM" }))]
              .slice(0, 4)
              .map((it) => (
                <button
                  key={it.id}
                  data-testid={`opp-${it.id}`}
                  onClick={() => nav(`/feeds/${it._src.toLowerCase()}`)}
                  className="w-full text-left p-3 rounded-lg border border-slate-100 hover:border-[#0e6939] hover:bg-[var(--green-50)] transition"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--green-700)" }}>
                      {it._src}
                    </span>
                    <span className="text-[10px] muted">· {it.ref_number}</span>
                  </div>
                  <div className="text-sm font-medium line-clamp-2">
                    {it.bid_details}
                  </div>
                  <div className="text-xs muted mt-1 truncate">
                    {it.entity_name || it.organization}
                  </div>
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Expiring certificates */}
      {expiringDocs.length > 0 && (
        <div className="card card-padded mt-5" data-testid="card-expiring">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="h2 flex items-center gap-2">
                <AlertCircle size={16} style={{ color: "#b45309" }} />
                Expiring certificates
              </div>
              <div className="text-xs muted">
                {expiringDocs.length} document{expiringDocs.length === 1 ? "" : "s"} expiring in the next 60 days
              </div>
            </div>
            <button
              onClick={() => nav("/documents")}
              className="text-xs muted hover:text-[#0e6939] flex items-center gap-1"
              data-testid="view-vault"
            >
              Open Vault <ArrowRight size={11} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {expiringDocs.slice(0, 6).map((d) => {
              const td = (function () {
                const t = new Date(d.expiry_date);
                if (isNaN(t)) return null;
                return Math.round(
                  (t.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) /
                    86400000
                );
              })();
              let bg = "var(--green-50)", color = "var(--green-700)";
              if (td !== null && td < 0) { bg = "#fef2f2"; color = "#b91c1c"; }
              else if (td !== null && td <= 7) { bg = "#fef2f2"; color = "#b91c1c"; }
              else if (td !== null && td <= 30) { bg = "#fef3c7"; color = "#92400e"; }
              else { bg = "#fef3c7"; color = "#92400e"; }
              return (
                <div
                  key={d.id}
                  data-testid={`expiring-${d.id}`}
                  className="p-3 rounded-lg border border-slate-100 hover:border-[#0e6939] transition"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={13} style={{ color: "var(--green-700)" }} />
                    <span className="text-xs font-bold truncate" style={{ color: "var(--green-700)" }}>
                      {d.category || d.folder}
                    </span>
                  </div>
                  <div className="text-sm font-semibold truncate">{d.file_name}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] muted">{d.expiry_date}</span>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                      style={{ background: bg, color }}
                    >
                      {td === null ? "—" : td < 0 ? `Expired ${-td}d ago` : `${td}d left`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Google Drive backup status (admin only) */}
      {user?.role === "admin" && backup && (
        <div className="card card-padded mt-5" data-testid="card-backup">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="h2 flex items-center gap-2">
                {backup.last_success && !backup.last_error?.at ? (
                  <CheckCircle2 size={16} style={{ color: "var(--green-700)" }} />
                ) : backup.last_error ? (
                  <CloudOff size={16} style={{ color: "#b91c1c" }} />
                ) : (
                  <Cloud size={16} style={{ color: "var(--green-700)" }} />
                )}
                Google Drive backup
              </div>
              <div className="text-xs muted mt-1">
                {backup.cadence} · folder “{backup.folder}”
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="btn-backup-download"
                disabled={backupBusy}
                onClick={async () => {
                  setBackupBusy(true);
                  try {
                    const res = await api.downloadBackup();
                    const url = window.URL.createObjectURL(res.data);
                    const link = document.createElement("a");
                    link.href = url;
                    const cd = res.headers?.["content-disposition"] || "";
                    const m = cd.match(/filename="?([^"]+)"?/);
                    link.download = m ? m[1] : "jero-backup.zip";
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast("Backup downloaded", "success");
                    api.backupStatus().then(setBackup).catch(() => {});
                  } catch (e) {
                    toast(e?.response?.data?.detail || "Download failed", "error");
                  } finally {
                    setBackupBusy(false);
                  }
                }}
                className="btn btn-ghost btn-sm"
                title="Download a ZIP of every collection to your device"
              >
                Download ZIP
              </button>
              <button
                data-testid="btn-backup-now"
                disabled={backupBusy}
                onClick={async () => {
                  setBackupBusy(true);
                  try {
                    await api.runBackup();
                    toast("Backup queued — checking status in a few seconds…", "success");
                    setTimeout(() => {
                      api.backupStatus().then(setBackup).catch(() => {});
                      setBackupBusy(false);
                    }, 6000);
                  } catch (e) {
                    toast(e?.response?.data?.detail || "Backup failed to queue", "error");
                    setBackupBusy(false);
                  }
                }}
                className="btn btn-primary btn-sm"
              >
                {backupBusy ? "Working…" : "Run backup now"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="p-3 rounded-lg border border-slate-100">
              <div className="muted uppercase font-semibold text-[10px] mb-1">Last successful</div>
              {backup.last_success ? (
                <>
                  <div className="font-semibold">{backup.last_success.file_name}</div>
                  <div className="muted">{new Date(backup.last_success.at).toLocaleString()}</div>
                  {backup.last_success.webViewLink && (
                    <a
                      href={backup.last_success.webViewLink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#0e6939] font-semibold text-[11px]"
                    >
                      View in Drive →
                    </a>
                  )}
                </>
              ) : (
                <div className="muted">No successful backup yet.</div>
              )}
            </div>
            <div className="p-3 rounded-lg border border-slate-100">
              <div className="muted uppercase font-semibold text-[10px] mb-1">Last attempt</div>
              {backup.last_error ? (
                <>
                  <div className="font-semibold text-[#b91c1c]">Failed · {new Date(backup.last_error.at).toLocaleString()}</div>
                  {backup.last_error.hint ? (
                    <div className="text-[11px] mt-1" style={{ color: "#b91c1c" }}>
                      {backup.last_error.hint.split(/(https?:\/\/[^\s]+)/g).map((chunk, i) =>
                        chunk.startsWith("http") ? (
                          <a
                            key={`hint-a-${i}`}
                            href={chunk}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                          >
                            {chunk}
                          </a>
                        ) : (
                          <span key={`hint-s-${i}`}>{chunk}</span>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="text-[11px] muted mt-1 line-clamp-3">{backup.last_error.error}</div>
                  )}
                </>
              ) : (
                <div className="muted">No errors recorded.</div>
              )}
            </div>
          </div>

          {!backup.drive_connected && (
            <div className="mt-3 text-[11px]" style={{ color: "#b45309" }}>
              Google Drive is not connected. Go to Emails → Connect Google to enable backups.
            </div>
          )}
        </div>
      )}

      {/* WhatsApp notifications (admin only) */}
      {user?.role === "admin" && wa && (
        <div className="card card-padded mt-5" data-testid="card-whatsapp">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="h2 flex items-center gap-2">
                <MessageCircle
                  size={16}
                  style={{ color: wa.enabled ? "var(--green-700)" : "#b45309" }}
                />
                WhatsApp notifications
              </div>
              <div className="text-xs muted mt-1">
                {wa.enabled ? (
                  <>Enabled · sending from <b>{wa.from_number}</b></>
                ) : (
                  <>
                    Scaffolded — waiting on Meta Business Cloud API credentials
                    for <b>{wa.from_number || "+256 781 830 835"}</b>
                  </>
                )}
              </div>
            </div>
            <button
              data-testid="btn-wa-test"
              disabled={waTesting}
              onClick={async () => {
                const to = prompt(
                  "WhatsApp number to test (e.g. +256781830835):",
                  wa.from_number || ""
                );
                if (!to) return;
                setWaTesting(true);
                try {
                  const r = await api.whatsappTest({
                    to,
                    event: "created",
                    serial: "TEST/2026/000",
                    title: "Portal test message",
                  });
                  toast(
                    r.status === "sent"
                      ? `Test sent · id ${r.message_id?.slice(0, 24) || ""}`
                      : `${r.status.toUpperCase()}: ${r.error || "see log"}`,
                    r.status === "sent" ? "success" : "error"
                  );
                  api.whatsappStatus().then(setWa).catch(() => {});
                } finally {
                  setWaTesting(false);
                }
              }}
              className="btn btn-primary btn-sm"
            >
              {waTesting ? "Sending…" : "Send test"}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs mb-3">
            <div className="p-3 rounded-lg border border-slate-100">
              <div className="muted uppercase font-semibold text-[10px] mb-2">
                Setup checklist
              </div>
              <ul className="space-y-1.5">
                <Check ok={!!wa.from_number} label={`FROM number set (${wa.from_number || "—"})`} />
                <Check ok={wa.phone_number_id_set} label="WHATSAPP_PHONE_NUMBER_ID in .env" />
                <Check ok={wa.access_token_set} label="WHATSAPP_ACCESS_TOKEN in .env" />
                <Check ok={wa.enabled} label="WHATSAPP_ENABLED=1" />
              </ul>
              {!wa.enabled && (
                <div className="mt-2 text-[11px]" style={{ color: "var(--ink-700)" }}>
                  Once all 4 rows are green, ticket events auto-send via WhatsApp.
                  <br />
                  <a
                    href={wa.setup_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                    style={{ color: "var(--green-700)" }}
                  >
                    Approve templates on Meta Business Manager →
                  </a>
                </div>
              )}
            </div>
            <div className="p-3 rounded-lg border border-slate-100">
              <div className="muted uppercase font-semibold text-[10px] mb-2">Recent activity</div>
              {wa.last_success ? (
                <div className="text-[11px]">
                  <b style={{ color: "var(--green-700)" }}>✓ Sent</b>{" "}
                  {new Date(wa.last_success.at).toLocaleString()} to{" "}
                  <code>{wa.last_success.to}</code>
                </div>
              ) : (
                <div className="text-[11px] muted">No successful sends yet.</div>
              )}
              {wa.last_error && (
                <div className="text-[11px] mt-1" style={{ color: "#b91c1c" }}>
                  <b>✗ Error</b> {new Date(wa.last_error.at).toLocaleString()} —{" "}
                  {wa.last_error.error?.slice(0, 200)}
                </div>
              )}
            </div>
          </div>

          <details className="text-[11px]">
            <summary className="cursor-pointer muted hover:text-slate-900 font-semibold">
              📋 Templates to approve in Meta Business Manager ({(wa.templates || []).length})
            </summary>
            <div className="mt-2 space-y-2">
              {(wa.templates || []).map((t) => (
                <div key={t.name} className="p-2 rounded border border-slate-100">
                  <div className="font-mono font-semibold text-[10px]" style={{ color: "var(--green-700)" }}>
                    {t.name} · {t.category} · {t.language}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-[11px]" style={{ color: "var(--ink-700)" }}>
                    {t.body}
                  </div>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Recent bids */}
      <div className="card" data-testid="card-recent">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <div className="h2">Recent bids</div>
          <button
            onClick={() => nav("/bids")}
            className="text-xs muted hover:text-[#0e6939] flex items-center gap-1"
          >
            View all <ArrowRight size={11} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>Title</th>
                <th>Client</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Amount</th>
                <th>Deadline</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_bids.map((b) => (
                <tr
                  key={b.id}
                  data-testid={`recent-${b.id}`}
                  className="cursor-pointer"
                  onClick={() => nav(`/bids?id=${b.id}`)}
                >
                  <td>
                    <div className="font-semibold">{b.title}</div>
                    <div className="text-xs muted">{b.ref_number || "—"}</div>
                  </td>
                  <td>{b.client_name || "—"}</td>
                  <td>
                    <BidStatus status={b.status} />
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>
                    {formatCurrency(b.bid_amount, b.currency)}
                  </td>
                  <td>{formatDate(b.deadline)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Check({ ok, label }) {
  return (
    <li className="flex items-center gap-2">
      <span
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[9px] font-bold"
        style={{ background: ok ? "var(--green-700)" : "#cbd5e1" }}
      >
        {ok ? "✓" : "•"}
      </span>
      <span style={{ color: ok ? "var(--ink-900)" : "var(--ink-700)" }}>{label}</span>
    </li>
  );
}


function StatCard({ icon: Icon, label, value, sub, tone = "slate", testId }) {
  const tones = {
    slate: { bg: "var(--ink-100)", fg: "var(--ink-700)" },
    green: { bg: "var(--green-50)", fg: "var(--green-700)" },
    emerald: { bg: "var(--emerald-bg)", fg: "var(--emerald-fg)" },
    amber: { bg: "var(--amber-bg)", fg: "var(--amber-fg)" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className="stat" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="stat-label">{label}</div>
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: t.bg, color: t.fg }}
        >
          <Icon size={16} />
        </div>
      </div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}
